import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Building2, TrendingUp, Shield, Share2, Target, Play, Check } from 'lucide-react';

import { BROKER_TIERS, getCurrentTier } from '@/config/brokerTiers';

// Broker Tier Configuration (Based on Cumulative Capital Raised)
// Bronze: ₹0, Silver: ₹25L, Gold: ₹75L, Platinum: ₹2.25Cr, Diamond: ₹6.75Cr
const tiers = BROKER_TIERS.map(t => ({
  name: t.name, rate: t.displayRate, icon: t.key === 'bronze' ? '🥉' : t.key === 'silver' ? '🥈' : t.key === 'gold' ? '🥇' : t.key === 'platinum' ? '💎' : '👑',
  color: t.color, bgColor: t.bg, threshold: t.threshold
}));

export default function BrokerDemo() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [calculatorAmount, setCalculatorAmount] = useState(5000000); // 50L
  const [cumulativeCapital, setCumulativeCapital] = useState(3000000); // 30L existing business

  const formatINR = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Separate formatter for precise commission display
  const formatCommission = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
    return `₹${Math.round(amount).toLocaleString('en-IN')}`;
  };

    const currentTier = getCurrentTier(cumulativeCapital + calculatorAmount);

  const steps = [
    {
      id: 'welcome',
      title: 'Partner with ALTX',
      subtitle: 'Broker Partnership Program',
      content: 'Earn commissions by connecting your network to verified real estate investment opportunities. Professional tools, transparent processes, and competitive payouts.',
      icon: Building2,
    },
    {
      id: 'concept',
      title: 'What is Fractional Real Estate?',
      subtitle: 'The Foundation',
      content: 'Understanding how fractional ownership works is key to explaining it to your investors. Watch this explainer to learn about LLP structures, ownership rights, and investor protections.',
      type: 'video',
      icon: Play,
    },
    {
      id: 'broker-role',
      title: 'Your Role as a Partner Broker',
      subtitle: 'Simple & Focused',
      content: 'Source investors for pre-vetted properties. We handle operations, legal documentation, property management, and investor servicing. You focus exclusively on relationships and deal flow.',
      icon: Target,
    },
    {
      id: 'earnings',
      title: 'Commission Structure',
      subtitle: 'Tier-Based Earnings',
      content: 'Your commission rate increases as you raise more capital. Move the slider to see how much you would earn at each tier level. Higher tiers unlock better rates and exclusive benefits.',
      type: 'calculator',
      icon: TrendingUp,
    },
    {
      id: 'sharing',
      title: 'Share Opportunities Instantly',
      subtitle: 'Investor Engagement Tools',
      content: 'Generate personalized investment links for each opportunity. Share via WhatsApp, SMS, or email. Track engagement metrics in real-time and follow up with interested investors at optimal moments.',
      type: 'sharing-detail',
      icon: Share2,
    },
    {
      id: 'escrow',
      title: 'Transparent Money Flow',
      subtitle: 'Security & Compliance',
      content: 'All investor funds flow through regulated escrow accounts. Funds are released only when verified construction milestones are completed. Your commissions are calculated automatically and paid monthly.',
      type: 'escrow-flow',
      icon: Shield,
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      navigate('/broker/demo/dashboard-tour');
    }
  };

  const currentSlide = steps[currentStep];
  const Icon = currentSlide.icon;

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
      <div className="max-w-6xl w-full">
        {/* Progress Bar */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs text-gray-600 uppercase tracking-widest font-medium">Demo Progress</span>
            <span className="text-xs text-gray-600 font-mono">{currentStep + 1}/{steps.length}</span>
          </div>
          <div className="w-full h-px bg-white/10 relative">
            <motion.div 
              className="absolute top-0 left-0 h-full bg-[#4ADE80]"
              initial={{ width: 0 }}
              animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white/5 border border-white/10 p-16"
          >
            {/* Icon */}
            <motion.div 
              className="w-10 h-10 bg-[#4ADE80]/10 flex items-center justify-center mb-8"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <Icon className="w-5 h-5 text-[#4ADE80]" />
            </motion.div>

            {/* Content */}
            <p className="text-xs text-gray-500 font-medium mb-4 uppercase tracking-widest">{currentSlide.subtitle}</p>
            <h2 className="text-3xl font-light text-white mb-6 leading-tight max-w-3xl" style={{ letterSpacing: '-0.02em' }}>
              {currentSlide.title}
            </h2>
            <p className="text-base text-gray-400 leading-relaxed mb-16 max-w-2xl">
              {currentSlide.content}
            </p>

            {/* Interactive Components */}
            {currentSlide.type === 'video' && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="bg-black border border-white/20 aspect-video mb-16 flex items-center justify-center max-w-4xl hover:border-white/40 transition-colors cursor-pointer group"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-white/5 border border-white/10 flex items-center justify-center mb-4 mx-auto group-hover:bg-white/10 transition-all">
                    <Play className="w-10 h-10 text-white/40 group-hover:text-white/60 transition-colors" />
                  </div>
                  <p className="text-sm text-gray-500">Fractional Real Estate Explainer</p>
                  <p className="text-xs text-gray-700 mt-2">YouTube video will be embedded here</p>
                </div>
              </motion.div>
            )}

            {currentSlide.type === 'calculator' && (
              <div className="bg-white/5 border border-white/10 p-8 mb-16 max-w-5xl">
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Left Side - Slider */}
                  <div>
                    <p className="text-xs text-gray-500 mb-4 uppercase tracking-widest">Capital you raise</p>
                    <input
                      type="range"
                      min={1000000}
                      max={10000000}
                      step={500000}
                      value={calculatorAmount}
                      onChange={(e) => setCalculatorAmount(parseInt(e.target.value))}
                      className="w-full h-px bg-white/10 appearance-none cursor-pointer slider-thumb mb-4"
                      style={{
                        background: `linear-gradient(to right, #4ADE80 0%, #4ADE80 ${((calculatorAmount - 1000000) / 9000000) * 100}%, rgba(255,255,255,0.1) ${((calculatorAmount - 1000000) / 9000000) * 100}%, rgba(255,255,255,0.1) 100%)`
                      }}
                    />
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">₹10L</span>
                      <motion.span 
                        key={calculatorAmount}
                        initial={{ scale: 0.95 }}
                        animate={{ scale: 1 }}
                        className="text-3xl font-light text-white"
                      >
                        {formatINR(calculatorAmount)}
                      </motion.span>
                      <span className="text-xs text-gray-600">₹1Cr</span>
                    </div>
                  </div>

                  {/* Right Side - Tier Earnings Table */}
                  <div>
                    <p className="text-xs text-gray-500 mb-4 uppercase tracking-widest">Earnings by tier</p>
                    <div className="space-y-2">
                      {tiers.map((tier, idx) => {
                        const earnings = (calculatorAmount * tier.rate) / 100;
                        
                        return (
                          <motion.div
                            key={tier.name}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: idx * 0.05 }}
                            className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors px-3"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-base">{tier.icon}</span>
                              <div>
                                <p className={`text-sm font-medium ${tier.color}`}>{tier.name}</p>
                                <p className="text-xs text-gray-600 font-mono">{tier.rate}%</p>
                              </div>
                            </div>
                            <motion.p 
                              key={earnings}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="text-sm font-mono text-white"
                            >
                              {formatCommission(earnings)}
                            </motion.p>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentSlide.type === 'sharing-detail' && (
              <div className="bg-white/5 border border-white/10 p-10 mb-16 max-w-5xl">
                <div className="grid md:grid-cols-2 gap-12">
                  <div>
                    <h4 className="text-lg font-medium text-white mb-6 tracking-tight">How Link Sharing Works</h4>
                    <div className="space-y-6">
                      {[
                        {
                          step: '1',
                          title: 'Generate Unique Link',
                          desc: 'Click "Share" on any property. System generates a unique URL with your broker ID embedded.'
                        },
                        {
                          step: '2',
                          title: 'Share via Multiple Channels',
                          desc: 'Send via WhatsApp, SMS, or email with pre-written templates. One-click sharing to your entire network.'
                        },
                        {
                          step: '3',
                          title: 'Track in Real-Time',
                          desc: 'Dashboard shows clicks, page views, time spent, and conversion actions. Know exactly who\'s interested.'
                        },
                        {
                          step: '4',
                          title: 'Auto-Attribution',
                          desc: 'Any investment through your link is automatically credited to you. No manual tracking needed.'
                        }
                      ].map((item, idx) => (
                        <motion.div 
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className="flex gap-4 group hover:translate-x-1 transition-transform"
                        >
                          <div className="flex-shrink-0 w-6 h-6 bg-[#4ADE80]/20 flex items-center justify-center text-xs text-[#4ADE80] font-mono">
                            {item.step}
                          </div>
                          <div>
                            <p className="text-sm text-white mb-1 font-medium">{item.title}</p>
                            <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white mb-6 tracking-tight">Analytics Dashboard</h4>
                    <div className="bg-black border border-white/10 p-6">
                      <div className="space-y-5">
                        {[
                          { label: 'Links Shared', value: '24', suffix: 'properties' },
                          { label: 'Total Clicks', value: '186', suffix: 'clicks' },
                          { label: 'Unique Viewers', value: '142', suffix: 'people' },
                          { label: 'Conversions', value: '31', highlight: true },
                          { label: 'Conversion Rate', value: '16.7%', highlight: true },
                        ].map((stat, idx) => (
                          <div key={idx} className="flex justify-between items-center pb-4 border-b border-white/5 last:border-0 last:pb-0">
                            <span className="text-xs text-gray-500 uppercase tracking-wider">{stat.label}</span>
                            <div className="text-right">
                              <span className={`text-xl font-mono ${
                                stat.highlight ? 'text-[#4ADE80]' : 'text-white'
                              }`}>
                                {stat.value}
                              </span>
                              {stat.suffix && (
                                <span className="text-xs text-gray-600 ml-2">{stat.suffix}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentSlide.type === 'escrow-flow' && (
              <div className="bg-white/5 border border-white/10 p-10 mb-16 max-w-4xl">
                <div className="space-y-6">
                  {[
                    {
                      step: '1',
                      title: 'Investor Payment',
                      desc: 'Funds transferred to secure, regulated escrow account',
                      detail: 'Verified by independent escrow provider',
                      amount: '₹10,00,000'
                    },
                    {
                      step: '2',
                      title: 'Milestone Verification',
                      desc: 'Construction progress verified by independent third-party engineer',
                      detail: 'Photos, reports, and documentation uploaded'
                    },
                    {
                      step: '3',
                      title: 'Funds Released',
                      desc: 'Payment released to builder for verified milestone',
                      detail: 'Your commission auto-calculated'
                    },
                    {
                      step: '4',
                      title: 'Monthly Commission Payout',
                      desc: 'Commission credited to your registered bank account',
                      detail: 'Detailed payout statement provided',
                      amount: '₹58,300'
                    }
                  ].map((item, idx) => (
                    <motion.div 
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.15 }}
                      className="flex items-start gap-5 pb-6 border-b border-white/10 last:border-0 hover:translate-x-1 transition-transform"
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-[#4ADE80]/20 flex items-center justify-center text-sm text-[#4ADE80] font-mono">
                        {item.step}
                      </div>
                      <div className="flex-1">
                        <p className="text-base text-white font-medium mb-2">{item.title}</p>
                        <p className="text-sm text-gray-400 mb-1">{item.desc}</p>
                        <p className="text-xs text-gray-600">{item.detail}</p>
                      </div>
                      {item.amount && (
                        <p className="text-[#4ADE80] font-mono text-sm">{item.amount}</p>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-12 border-t border-white/10">
              <button
                onClick={() => navigate('/broker')}
                className="text-gray-600 hover:text-gray-400 transition-colors text-xs uppercase tracking-widest font-medium"
              >
                Exit Demo
              </button>
              
              <div className="flex items-center gap-4">
                {currentStep > 0 && (
                  <Button
                    onClick={() => setCurrentStep(currentStep - 1)}
                    variant="outline"
                    className="border border-white/20 hover:border-white/40 hover:bg-white/5 bg-transparent text-white text-sm px-6 py-2 transition-all"
                  >
                    Back
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  className="bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-semibold text-sm px-6 py-2 hover:shadow-lg hover:shadow-[#4ADE80]/20 transition-all"
                >
                  {currentStep === steps.length - 1 ? 'Explore Dashboard' : 'Continue'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Step Indicators */}
            <div className="flex items-center justify-center gap-3 mt-10">
              {steps.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentStep(idx)}
                  className={`h-px transition-all hover:bg-white/40 ${
                    idx === currentStep ? 'w-16 bg-[#4ADE80]' : idx < currentStep ? 'w-8 bg-white/40' : 'w-8 bg-white/20'
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
